import {
  computeWaitingForMapping,
  getWarningPriority,
  EHR_MATCHABLE_MESSAGE_TYPES,
  WARNING_PRIORITY,
  type TblPatientPending,
  type WarningPriority,
} from '../../types/emr';

/**
 * Plain-English status for the Pending Messages screen: why a row needs
 * action, what to do about it, how many rows of each kind there are, and how
 * often the list refreshes. Shared by the grid, its summary line and the tab
 * badge on EmrPage so all three always agree.
 */

type PendingRowLike = Pick<
  TblPatientPending,
  'pKey_Patient_Pending' | 'processError' | 'hasMappingError' | 'matchFound' | 'fKey_User_Lock' | 'fKey_Ehr_MessageType'
>;

/** A row the user has to do something about (the "Action Needed" view). */
export function isActionNeeded(m: PendingRowLike, waitingIds: ReadonlySet<number>): boolean {
  return !m.processError && (m.hasMappingError || !m.matchFound || waitingIds.has(m.pKey_Patient_Pending));
}

/** The row's warning state, with the waiting-behind-an-earlier-message cascade applied. */
export function rowPriority(m: TblPatientPending, waitingIds: ReadonlySet<number>): WarningPriority {
  return waitingIds.has(m.pKey_Patient_Pending) ? WARNING_PRIORITY.WAITING_FOR_MAPPING : getWarningPriority(m);
}

export interface PendingSummary {
  /** Rows that have not failed processing (the "All" view). */
  pendingCount: number;
  /** Rows that failed processing (the "Errors" view). */
  errorCount: number;
  /** Rows in the "Action Needed" view. */
  actionNeededCount: number;
  /** Matchable rows nobody has matched to a resident yet: the user picks one. */
  noMatchCount: number;
  /** Rows that match nobody yet and can't be matched by hand (an admission, say). */
  notMatchedYetCount: number;
  /** Rows with values that need mapping. */
  mappingCount: number;
  /** Rows held back by an earlier message for the same person. */
  waitingCount: number;
}

export function summarizePending(
  messages: readonly TblPatientPending[],
  waitingIds: ReadonlySet<number> = computeWaitingForMapping(messages as TblPatientPending[]),
): PendingSummary {
  const s: PendingSummary = {
    pendingCount: 0, errorCount: 0, actionNeededCount: 0,
    noMatchCount: 0, notMatchedYetCount: 0, mappingCount: 0, waitingCount: 0,
  };
  for (const m of messages) {
    if (m.processError) { s.errorCount++; continue; }
    s.pendingCount++;
    if (!isActionNeeded(m, waitingIds)) continue;
    s.actionNeededCount++;
    // Same order the grid's icon uses, so each row is counted once under the reason it shows.
    if (waitingIds.has(m.pKey_Patient_Pending)) s.waitingCount++;
    else if (m.hasMappingError) s.mappingCount++;
    else if (isMatchable(m)) s.noMatchCount++;
    else s.notMatchedYetCount++;
  }
  return s;
}

function isMatchable(m: Pick<TblPatientPending, 'fKey_Ehr_MessageType'>): boolean {
  return EHR_MATCHABLE_MESSAGE_TYPES.has(m.fKey_Ehr_MessageType ?? 0);
}

export interface RowReason {
  /** A few words saying why, and where it can, what to do: the grid's Action column. */
  label: string;
  /** What to do, one short sentence: the icon's tooltip. */
  todo: string;
}

/**
 * Why a row is flagged and what to do, in words. `null` for a row that needs
 * nothing (it processes on its own).
 */
export function describePendingRow(
  m: TblPatientPending,
  priority: WarningPriority,
  residentLabel: string,
  lockedBy?: string,
): RowReason | null {
  const resident = residentLabel.toLowerCase();
  switch (priority) {
    case WARNING_PRIORITY.MAPPING_ERROR:
      return { label: 'Needs mapping', todo: 'Open it and map the values marked in red.' };
    case WARNING_PRIORITY.LOCKED:
      return { label: `Open by ${lockedBy || 'another user'}`, todo: 'Someone is working on it now.' };
    case WARNING_PRIORITY.NO_MATCH:
      return isMatchable(m)
        ? { label: `Needs a ${resident} match`, todo: `Open it and choose the ${resident}, or add them as new.` }
        : { label: 'Not matched yet', todo: 'Dietech tries again shortly. Open it to review.' };
    case WARNING_PRIORITY.WAITING_FOR_MAPPING:
      return { label: 'Waiting on earlier message', todo: `It processes on its own once the earlier message for this ${resident} is fixed.` };
    default:
      return null;
  }
}

/**
 * The summary line over the grid: one sentence per kind of problem, each
 * saying what to do. Empty when nothing needs action.
 */
export function summaryLines(s: PendingSummary, residentLabel: string): string[] {
  const resident = residentLabel.toLowerCase();
  const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
  const lines: string[] = [];
  if (s.noMatchCount > 0) {
    lines.push(
      `${n(s.noMatchCount, `message doesn't match a ${resident}`, `messages don't match a ${resident}`)}. `
      + `Open ${s.noMatchCount === 1 ? 'it' : 'each one'} and choose the ${resident}, or add them as new.`,
    );
  }
  if (s.mappingCount > 0) {
    lines.push(
      `${n(s.mappingCount, 'message has', 'messages have')} values that need mapping. `
      + `Open ${s.mappingCount === 1 ? 'it' : 'each one'} and map the values marked in red.`,
    );
  }
  if (s.notMatchedYetCount > 0) {
    lines.push(
      `${n(s.notMatchedYetCount, `message isn't`, `messages aren't`)} matched to a ${resident} yet. `
      + 'Dietech tries again shortly; open it to review if it stays.',
    );
  }
  if (s.waitingCount > 0) {
    lines.push(
      `${n(s.waitingCount, 'message is', 'messages are')} waiting behind an earlier message for the same ${resident}. `
      + `${s.waitingCount === 1 ? 'It processes' : 'They process'} on ${s.waitingCount === 1 ? 'its' : 'their'} own once that one is fixed.`,
    );
  }
  return lines;
}

// ===== AUTO REFRESH =====

/** Used when the facility has no usable refresh setting. */
export const DEFAULT_AUTO_REFRESH_SECONDS = 60;
export const MIN_AUTO_REFRESH_SECONDS = 5;
/**
 * Longest wait between refreshes: five minutes, the desktop program's default
 * for this setting. The same facility setting (`EHR_AutoDuration`) is also the
 * desktop EHR processor's pause between passes, and a facility processed by the
 * hosted EHR service can carry a huge value there (Kansas Heart has 500,000
 * seconds) to keep a desktop copy from processing. Used as-is, the page showed
 * "next refresh in 499979s" and never refreshed on its own.
 */
export const MAX_AUTO_REFRESH_SECONDS = 300;

/** The facility's refresh setting, made safe to drive a page timer. */
export function resolveAutoRefreshSeconds(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (raw == null || raw === '' || !Number.isFinite(n) || n <= 0) return DEFAULT_AUTO_REFRESH_SECONDS;
  return Math.min(MAX_AUTO_REFRESH_SECONDS, Math.max(MIN_AUTO_REFRESH_SECONDS, Math.round(n)));
}

/** Reads `EHR_AutoDuration` off the facility settings whatever its casing. */
export function autoRefreshSecondsFromSettings(settings: unknown): number {
  if (!settings || typeof settings !== 'object') return DEFAULT_AUTO_REFRESH_SECONDS;
  // System.Text.Json's first-char-lower policy writes `eHR_AutoDuration`, not the
  // `ehR_AutoDuration` one might guess, so match the key without regard to case.
  for (const [k, v] of Object.entries(settings as Record<string, unknown>)) {
    if (k.toLowerCase() === 'ehr_autoduration') return resolveAutoRefreshSeconds(v);
  }
  return DEFAULT_AUTO_REFRESH_SECONDS;
}

/** "45s", "1m 05s": the countdown. */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

/** "30 seconds", "1 minute", "5 minutes", "90 seconds": the interval itself. */
export function formatInterval(seconds: number): string {
  if (seconds >= 60 && seconds % 60 === 0) {
    const m = seconds / 60;
    return `${m} ${m === 1 ? 'minute' : 'minutes'}`;
  }
  return `${seconds} seconds`;
}
